/**
 * TikTok Pixel + Events API conversion tracking
 *
 * Fires browser ttq.track events aligned with Meta funnel events (see metaTracking.js).
 * Also POSTs the same events to WordPress (`action=trigger_tiktok_event`) using the
 * TikTok Events API envelope (event_source, data[].user, data[].page, properties).
 * Uses the same event_id as Meta for cross-platform dedup consistency in Everflow adv5.
 *
 * PII is sent plaintext — WordPress should SHA-256 hash email/phone/external_id
 * before forwarding to TikTok (same pattern as meta-capi).
 */

import { config } from '../config/env'
import { logger } from './logger'
import { createMetaEventId } from './metaEventId'
import { readUpsellAmount, resolveWhopUpsellPlan } from './whopTracking'
import { getTtclid, syncTikTokAdvancedMatching } from './tiktokPixel'
import {
  TIKTOK_EVENT_ADD_PAYMENT_INFO,
  TIKTOK_EVENT_ADD_PAYMENT_INFO_UPSELL,
  TIKTOK_EVENT_FIRST_PURCHASE,
  TIKTOK_EVENT_INITIATE_CHECKOUT,
  TIKTOK_EVENT_PURCHASE,
  TIKTOK_EVENT_QUIZ_COMPLETED,
  TIKTOK_EVENT_START_QUIZ,
} from './tiktokEventNames'

const TIKTOK_EVENTS_ENDPOINT = '/trigger-tiktok-event'

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? match[2] : null
}

const formatPhoneForTikTok = (phone) => {
  if (!phone) return null
  const digits = String(phone).replace(/\D/g, '')
  if (!digits) return null
  if (digits.length === 10) return `+1${digits}`
  if (digits.length >= 11) return `+${digits}`
  return null
}

const GLP1_PRODUCT_GROUP = {
  content_type:     'product_group',
  content_name:     'GLP-1',
  content_category: 'GLP-1',
  content_id:       'glp-1',
}

const HRT_PRODUCT_GROUP = {
  content_type:     'product_group',
  content_name:     'HRT',
  content_category: 'HRT',
  content_id:       'hrt',
}

const HRT_NAME_MARKERS = ['estradiol', 'progesterone', 'ir capsule', 'estriol', 'dhea', 'biest']

const isHrtFunnel = (funnelData = {}) =>
  String(funnelData.funnel_id || '').toLowerCase().includes('hrt')

const isHrtProductName = (value) => {
  const name = String(value || '').toLowerCase().replace(/_/g, ' ')
  return HRT_NAME_MARKERS.some((marker) => name.includes(marker))
}

const getViewContentProductGroup = (funnelData = {}) => {
  const funnelId = String(funnelData.funnel_id || '').toLowerCase()

  if (funnelId.includes('nad')) {
    return {
      content_type:     'product_group',
      content_name:     'NAD+',
      content_category: 'NAD+',
      content_id:       'nad',
    }
  }

  if (funnelId.includes('sermorelin')) {
    return {
      content_type:     'product_group',
      content_name:     'Sermorelin',
      content_category: 'Sermorelin',
      content_id:       'sermorelin',
    }
  }

  if (isHrtFunnel(funnelData) || isHrtProductName(funnelData.selected_product_name)) {
    return HRT_PRODUCT_GROUP
  }

  return GLP1_PRODUCT_GROUP
}

const parseTikTokValue = (price) => {
  if (typeof price === 'number' && !Number.isNaN(price)) return price
  if (typeof price === 'string') {
    const parsed = parseFloat(price.replace(/[$,]/g, ''))
    return Number.isNaN(parsed) ? 0 : parsed
  }
  return 0
}

const getFunnelValueFallback = (funnelData = {}) => {
  const funnelId = String(funnelData.funnel_id || '').toLowerCase()
  if (funnelId.includes('nad')) return 'nad'
  if (funnelId.includes('sermorelin')) return 'Sermorelin'
  if (funnelId.includes('hrt')) return 'hrt'
  return '1'
}

const readEventUpsellAmount = (funnelData = {}) => {
  const fromField = parseTikTokValue(funnelData.upsell_amount)
  if (fromField > 0) return fromField

  const fromSession = readUpsellAmount(funnelData.upsell_offer_id)
  if (fromSession != null) return fromSession

  return null
}

const getEventValue = (funnelData = {}, eventName) => {
  if (eventName === TIKTOK_EVENT_ADD_PAYMENT_INFO_UPSELL) {
    const upsellAmount = readEventUpsellAmount(funnelData)
    if (upsellAmount != null) return upsellAmount
  }

  const candidates = [
    funnelData.amount,
    funnelData.total_prescription_price,
    funnelData.selected_product_price,
    funnelData.display_order_total,
  ]

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === '') continue
    const parsed = parseTikTokValue(candidate)
    if (parsed > 0) return parsed
  }

  return getFunnelValueFallback(funnelData)
}

const getSelectedPlanQuery = (funnelData = {}, eventName) => {
  const plan = eventName === TIKTOK_EVENT_ADD_PAYMENT_INFO_UPSELL
    ? resolveWhopUpsellPlan(funnelData, {
        offerId: funnelData.upsell_offer_id,
        planType: funnelData.selected_plan || null,
      })
    : funnelData.selected_plan
  if (!plan || plan === 'unknown') return null
  return String(plan)
}

const buildProductGroupProperties = (funnelData) => ({
  currency: 'USD',
  query:    getSelectedPlanQuery(funnelData),
  ...getViewContentProductGroup(funnelData),
})

const SUBSCRIBE_PRODUCT_NAMES = {
  semaglutide: 'Semaglutide',
  tirzepatide: 'Tirzepatide',
  nad:         'NAD+',
  sermorelin:  'Sermorelin',
  zofran:      'Zofran',
  hrt:         'HRT',
}

const SUBSCRIBE_PRODUCT_CATEGORIES = {
  semaglutide: 'GLP-1',
  tirzepatide: 'GLP-1',
  nad:         'NAD+',
  sermorelin:  'Sermorelin',
  zofran:      'Zofran',
  hrt:         'HRT',
}

const getSubscribeProductSlug = (funnelData = {}) => {
  const medication = String(funnelData.selected_product_medication || '').toLowerCase()
  const name = String(funnelData.selected_product_name || funnelData.selected_product_id || '').toLowerCase()

  if (isHrtFunnel(funnelData) || isHrtProductName(name) || isHrtProductName(medication)) {
    return 'hrt'
  }

  if (medication) return medication.split('_')[0]
  if (name.includes('tirzepatide')) return 'tirzepatide'
  if (name.includes('semaglutide')) return 'semaglutide'
  if (name.includes('sermorelin')) return 'sermorelin'
  if (name.includes('nad')) return 'nad'
  if (name.includes('zofran')) return 'zofran'
  return ''
}

const buildSubscribeProperties = (funnelData, eventName) => {
  const group = getViewContentProductGroup(funnelData)
  const slug = getSubscribeProductSlug(funnelData)
  const contentId = slug || group.content_id
  const value = getEventValue(funnelData)
  const productName = String(funnelData.selected_product_name || '').trim()
  const hrtContentName = contentId === 'hrt' && productName ? productName : null

  return {
    currency:         'USD',
    value,
    query:            getSelectedPlanQuery(funnelData, eventName),
    content_type:     'product',
    content_name:     hrtContentName || SUBSCRIBE_PRODUCT_NAMES[contentId] || group.content_name,
    content_category: SUBSCRIBE_PRODUCT_CATEGORIES[contentId] || group.content_category,
    content_id:       contentId,
    quantity:         1,
    price:            value,
  }
}

const buildServerUser = (funnelData = {}) => ({
  email:        funnelData.email || null,
  phone:        formatPhoneForTikTok(funnelData.phone),
  external_id:  funnelData.entry_id || funnelData.customer_id || funnelData.case_id || null,
  ttclid:       getTtclid(funnelData),
  ttp:          getCookie('_ttp'),
  ip:           null,
  user_agent:   typeof navigator !== 'undefined' ? navigator.userAgent : null,
})

const buildServerProperties = (properties) => ({
  currency: null,
  value:    1,
  query:    null,
  ...properties,
})

const buildServerPage = () => ({
  url:      typeof window !== 'undefined' ? window.location.href : null,
  referrer: typeof document !== 'undefined' ? (document.referrer || null) : null,
})

const buildServerPayload = (eventName, properties, eventId, funnelData) => ({
  event_source:    'web',
  event_source_id: config.tiktokPixelId || null,
  data: [
    {
      event:      eventName,
      event_time: Math.floor(Date.now() / 1000),
      event_id:   eventId,
      user:       buildServerUser(funnelData),
      properties: buildServerProperties(properties),
      page:       buildServerPage(),
    },
  ],
})

const fireBrowserEvent = (eventName, properties, eventId, funnelData) => {
  if (!window.ttq) {
    logger.debug(`[TikTok] ttq not available, skipping ${eventName}`)
    return
  }

  const fire = async () => {
    if (funnelData) await syncTikTokAdvancedMatching(funnelData)
    window.ttq.track(eventName, properties, { event_id: eventId })
    logger.debug(`[TikTok] ${eventName} fired`, { eventId, properties })
  }

  fire().catch((err) => {
    logger.error(`[TikTok] ${eventName} failed:`, err)
  })
}

const fireServerEvent = async (eventName, properties, eventId, funnelData) => {
  const payload = buildServerPayload(eventName, properties, eventId, funnelData)

  try {
    const res = await fetch(TIKTOK_EVENTS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    let json = null
    try {
      json = await res.json()
    } catch (_) {
      json = null
    }

    if (!res.ok) {
      logger.warn(`[TikTok Events API] ${eventName} server error:`, json || res.status)
    } else {
      logger.debug(`[TikTok Events API] ${eventName} server success:`, json)
    }
  } catch (err) {
    logger.error(`[TikTok Events API] ${eventName} fetch error:`, err)
  }
}

const shouldTrackTikTok = () => {
  if (config.testMode) {
    logger.debug('[TikTok] Skipping — test/staging mode')
    return false
  }
  if (!config.tiktokPixelId) {
    logger.debug('[TikTok] Skipping — no VITE_TIKTOK_PIXEL_ID configured')
    return false
  }
  return true
}

const trackTikTokEvent = (eventName, properties, eventId, funnelData) => {
  if (!shouldTrackTikTok()) return

  let trackedProperties = properties
  if (eventName === TIKTOK_EVENT_ADD_PAYMENT_INFO_UPSELL) {
    const value = getEventValue(funnelData, eventName)
    trackedProperties = {
      ...properties,
      value,
      price: value,
      query: getSelectedPlanQuery(funnelData, eventName),
    }
  }

  // Same fallback as Meta: never send Pixel + Events API without a shared
  // event_id (required for web dedup). Callers that already pass Meta's
  // createMetaEventId() keep that value for Everflow adv5 alignment.
  const resolvedEventId = eventId || createMetaEventId()

  fireBrowserEvent(eventName, trackedProperties, resolvedEventId, funnelData)
  fireServerEvent(eventName, trackedProperties, resolvedEventId, funnelData)
}

/**
 * Start Quiz → ViewContent
 * @param {Object} funnelData
 * @param {string} [eventId]
 */
export const trackTikTokStartQuiz = (funnelData, eventId) => {
  console.log('trackTikTokStartQuiz', funnelData, eventId);
  trackTikTokEvent(
    TIKTOK_EVENT_START_QUIZ,
    buildProductGroupProperties(funnelData),
    eventId,
    funnelData,
  )
}

/**
 * Quiz Completed → AddToCart
 * @param {Object} funnelData
 * @param {string} [eventId]
 */
export const trackTikTokQuizCompleted = (funnelData, eventId) => {
  console.log('trackTikTokQuizCompleted', funnelData, eventId);
  trackTikTokEvent(
    TIKTOK_EVENT_QUIZ_COMPLETED,
    buildProductGroupProperties(funnelData),
    eventId,
    funnelData,
  )
}

/**
 * Payment page → InitiateCheckout
 * @param {Object} funnelData
 * @param {string} [eventId]
 */
export const trackTikTokInitiateCheckout = (funnelData, eventId) => {
  console.log('trackTikTokInitiateCheckout', funnelData, eventId);
  trackTikTokEvent(
    TIKTOK_EVENT_INITIATE_CHECKOUT,
    buildSubscribeProperties(funnelData),
    eventId,
    funnelData,
  )
}

/**
 * Checkout form submission → AddPaymentInfo
 * Fires browser pixel and Events API with the same event_id.
 * @param {Object} funnelData
 * @param {string} [eventId]
 */
export const trackTikTokAddPaymentInfo = (funnelData, eventId) => {
  trackTikTokEvent(
    TIKTOK_EVENT_ADD_PAYMENT_INFO,
    buildSubscribeProperties(funnelData),
    eventId,
    funnelData,
  )
}

/**
 * Upsell checkout form submission → AddPaymentInfoUpsell
 * Fires browser pixel and Events API with the same event_id.
 * @param {Object} funnelData
 * @param {string} [eventId]
 */
export const trackTikTokAddPaymentInfoUpsell = (funnelData, eventId) => {
  trackTikTokEvent(
    TIKTOK_EVENT_ADD_PAYMENT_INFO_UPSELL,
    buildSubscribeProperties(funnelData, TIKTOK_EVENT_ADD_PAYMENT_INFO_UPSELL),
    eventId,
    funnelData,
  )
}

/**
 * First Purchase → Subscribe (main product only)
 * @param {Object} funnelData
 * @param {string} [eventId]
 * @param {string} [orderId]
 */
export const trackTikTokFirstPurchase = (funnelData, eventId, orderId) => {
  console.log('trackTikTokFirstPurchase', funnelData, eventId, orderId);
  trackTikTokEvent(
    TIKTOK_EVENT_FIRST_PURCHASE,
    buildSubscribeProperties(funnelData),
    eventId,
    funnelData,
  )
}

/**
 * Purchase → CompletePayment (main + upsell)
 * @param {Object} funnelData
 * @param {string} [eventId]
 * @param {string} [orderId]
 */
export const trackTikTokPurchase = (funnelData, eventId, orderId) => {
  console.log('trackTikTokPurchase', funnelData, eventId, orderId);
  trackTikTokEvent(
    TIKTOK_EVENT_PURCHASE,
    buildSubscribeProperties(funnelData),
    eventId,
    funnelData,
  )
}

export default {
  trackTikTokStartQuiz,
  trackTikTokQuizCompleted,
  trackTikTokInitiateCheckout,
  trackTikTokAddPaymentInfo,
  trackTikTokAddPaymentInfoUpsell,
  trackTikTokFirstPurchase,
  trackTikTokPurchase,
}
